/*
 * @format
 */
import { MODES } from '../utils/consts';
import {
  isMatchingZoneId,
  humanizeTemperature,
  spokenTemperatureOrOff,
  spokenTime,
  getFollowingDay,
  getPreviousDay,
  getDayOfWeek,
  dateTimeToMinutesSinceMidnight,
  isHpScheduleEvent,
} from '../utils/helpers';
import {
  getPrtConnectionStatus,
  getTimeDisplayFormat,
  getZones,
} from '../selectors';
import {
  getMode,
  getPlusHoursEndtime,
  getZone,
  isInHolidayMode,
} from '../controls-selectors';
import {
  getEventProgress as scheduleGetEventProgress,
  getNextEvent as scheduleGetNextEvent,
  getCurrentEvent as scheduleGetCurrentEvent,
  flattenSchedule,
  weekifySchedule,
  unflattenSchedule,
} from '../schedule/schedule';
import { cloneDeep } from 'lodash';
import { getPendingData } from '../reducers/pending-reducer';

const getZonePropertyOrDefault = (
  state,
  homeId,
  zoneId,
  property,
  defaultValue,
) => {
  const home = state.homeList.homes.find(x => x.id === homeId);

  if (!home) {
    return defaultValue;
  }

  const zone = home.zones.find(x => isMatchingZoneId(x.zoneId, zoneId));

  if (!zone) {
    return defaultValue;
  }

  return zone[property];
};

export const getPendingSchedule = (state, unflatten, homeId, zoneId) => {
  const pendingSchedule = getPendingData(state.pendingSchedule, homeId, zoneId);

  if (pendingSchedule?.schedule) {
    if (unflatten) {
      // convert from flat array to nested arrays arranged by day and fill in the endTime
      return unflattenSchedule(pendingSchedule.schedule).map(x => ({
        ...x,
        events: x.events.map((y, i) => ({
          ...y,
          endTime:
            i + 1 < x.events.length ? x.events[i + 1].startTime : undefined,
        })),
      }));
    } else {
      return pendingSchedule.schedule;
    }
  }

  return undefined;
};

/**
 * Check if a schedule save is currently in progress for a specific zone
 * Returns true if there's a pending request that hasn't completed yet
 */
export const isScheduleSaveInProgress = (state, homeId, zoneId) => {
  const pendingSchedule = getPendingData(state.pendingSchedule, homeId, zoneId);
  // Request is in progress if it exists and requestId is not 'done'
  // Use Boolean() to ensure we return true/false, not undefined
  return Boolean(pendingSchedule && pendingSchedule.requestId !== 'done');
};

/**
 * Get the draft schedule for a specific zone
 * Draft schedules are local changes that haven't been submitted to the API yet
 */
export const getDraftSchedule = (state, homeId, zoneId) => {
  if (!state.draftSchedule || !state.draftSchedule.drafts) {
    return undefined;
  }

  if (!zoneId || !Array.isArray(zoneId) || zoneId.length < 2) {
    return undefined;
  }

  const draft = state.draftSchedule.drafts.find(
    d =>
      d.homeId === homeId &&
      d.zoneDeviceId === zoneId[0] &&
      d.endpointId === zoneId[1],
  );

  return draft?.schedule;
};

/**
 * Get the original schedule that was stored when the draft was created
 * This is used to determine what the actual server state was
 */
export const getDraftOriginalSchedule = (state, homeId, zoneId) => {
  if (!state.draftSchedule || !state.draftSchedule.drafts) {
    return undefined;
  }

  if (!zoneId || !Array.isArray(zoneId) || zoneId.length < 2) {
    return undefined;
  }

  const draft = state.draftSchedule.drafts.find(
    d =>
      d.homeId === homeId &&
      d.zoneDeviceId === zoneId[0] &&
      d.endpointId === zoneId[1],
  );

  return draft?.originalSchedule;
};

/**
 * Check if there are unsaved draft changes for a specific zone
 */
export const hasDraftScheduleChanges = (state, homeId, zoneId) => {
  const draftSchedule = getDraftSchedule(state, homeId, zoneId);
  return draftSchedule !== undefined;
};

const getWeekifiedSchedule = (state, homeId, zoneId) => {
  const zoneSchedule = getZonePropertyOrDefault(
    state,
    homeId,
    zoneId,
    'schedule',
    [],
  );
  const pendingSchedule = getPendingSchedule(state, true, homeId, zoneId);

  const schedule = pendingSchedule || zoneSchedule;

  return weekifySchedule(schedule);
};

export const getCurrentEvent = (state, homeId, zoneId) => {
  const schedule = getWeekifiedSchedule(state, homeId, zoneId);

  return scheduleGetCurrentEvent(schedule);
};

export const getNextEvent = (state, homeId, zoneId) => {
  const schedule = getWeekifiedSchedule(state, homeId, zoneId);
  const plusHoursEndtime = getPlusHoursEndtime(state, homeId, zoneId);
  const timeDisplayFormat = getTimeDisplayFormat(state, homeId, zoneId);

  const nextEvent = scheduleGetNextEvent(
    schedule,
    plusHoursEndtime,
    timeDisplayFormat,
  );

  return nextEvent;
};

export const getEventProgress = (state, homeId, zoneId) => {
  const schedule = getWeekifiedSchedule(state, homeId, zoneId);

  return scheduleGetEventProgress(schedule);
};

export const getHpSetpoint = (state, homeId, zoneId) => {
  const zone = getZone(state, homeId, zoneId);

  if (
    !zone ||
    !zone.comfortTemperatureSetpoint ||
    !zone.reducdedTemperatureSetpoint
  ) {
    // return default setpoints
    return { comfortSetpoint: 2100, reducedSetpoint: 1800 };
  }

  return {
    comfortSetpoint: zone.comfortTemperatureSetpoint,
    reducedSetpoint: zone.reducdedTemperatureSetpoint,
  };
};

/* ============================================================

  Schedule selectors taken from selectors.js - some may be redundant

============================================================ */

// The next event's temperature as the schedule panel shows it: "21.0°", or
// "OFF" for a frost-protection setpoint. The spoken forms are
// spokenTemperatureOrOff and spokenTime: "21.0° at 16:00" comes out as
// "twenty-one dot zero degrees at sixteen" on some voices.
const shownTemperature = setpoint =>
  humanizeTemperature(setpoint, true, false, true);

const nextScheduleEvent = (
  state,
  homeId,
  zoneId,
  { formatTemperature, formatTime },
) => {
  const nextEvent = getNextEvent(state, homeId, zoneId);
  const hpSetpoint = getHpSetpoint(state, homeId, zoneId);

  if (nextEvent != null) {
    if (!nextEvent.setpoint) {
      return `${nextEvent.on ? 'On' : 'Off'} at ${formatTime(nextEvent)}`;
    }

    const isComfort = nextEvent.setpoint === 'comfort';
    const isReduced = nextEvent.setpoint === 'reduced';

    let temp;

    if (isComfort) {
      temp = formatTemperature(hpSetpoint.comfortSetpoint);
    } else if (isReduced) {
      temp = formatTemperature(hpSetpoint.reducedSetpoint);
    } else {
      temp = formatTemperature(nextEvent.setpoint);
    }

    return `${temp} at ${formatTime(nextEvent)}`;
  }
  return 'No more events today';
};

export const getNextScheduleEvent = (state, homeId, zoneId) =>
  nextScheduleEvent(state, homeId, zoneId, {
    formatTemperature: shownTemperature,
    formatTime: event => event.displayTime,
  });

export const getSpokenNextScheduleEvent = (state, homeId, zoneId) => {
  const timeDisplayFormat = getTimeDisplayFormat(state, homeId, zoneId);

  return nextScheduleEvent(state, homeId, zoneId, {
    formatTemperature: spokenTemperatureOrOff,
    formatTime: event =>
      spokenTime(event.minutesSinceMidnight, timeDisplayFormat),
  });
};

// The next event's time on its own as a screen reader should read it, for
// the panels that show it inside their own line ("Override until 16:00",
// "Until 16:00"). Undefined when there is no next event.
export const getSpokenNextEventTime = (state, homeId, zoneId) => {
  const nextEvent = getNextEvent(state, homeId, zoneId);

  return nextEvent
    ? spokenTime(
        nextEvent.minutesSinceMidnight,
        getTimeDisplayFormat(state, homeId, zoneId),
      )
    : undefined;
};

const nextEventText = (state, homeId, zoneId, scheduleEvent) => {
  const mode = getMode(state, homeId, zoneId);
  const holidayModeActive = isInHolidayMode(state, homeId, zoneId);

  if (mode === MODES.Off || holidayModeActive) {
    return 'Inactive';
  }
  if (
    mode === MODES.Auto ||
    mode === MODES.OverrideNext ||
    mode === MODES.OverridePlusHours ||
    mode === MODES.DSRActive
  ) {
    return scheduleEvent(state, homeId, zoneId);
  }
  if (mode === MODES.Manual || mode === MODES.On) {
    return ''; // formally showed Active and Permantly On
  }
  return 'Mode Unknown';
};

// The schedule panel's sub line: the next event, or why there is none.
export const getNextEventText = (state, homeId, zoneId) =>
  nextEventText(state, homeId, zoneId, getNextScheduleEvent);

// The same line as a screen reader should read it, temperature in words.
export const getSpokenNextEventText = (state, homeId, zoneId) =>
  nextEventText(state, homeId, zoneId, getSpokenNextScheduleEvent);

export const getSchedule = (state, homeId, zoneId) => {
  const home = state.homeList.homes.find(x => x.id === homeId);
  const selectedZone = home?.zones?.find(x =>
    isMatchingZoneId(x.zoneId, zoneId),
  );

  if (!selectedZone) {
    return [];
  }

  // 1. Check for local draft (not yet sent to API) - highest priority
  const draftSchedule = getDraftSchedule(state, homeId, zoneId);
  if (draftSchedule) {
    return draftSchedule;
  }

  // 2. Check for pending (in-flight to API)
  const pendingSchedule = getPendingSchedule(state, false, homeId, zoneId);
  if (pendingSchedule) {
    return pendingSchedule;
  }

  // 3. Fall back to actual server schedule
  return flattenSchedule(selectedZone.schedule);
};

export const getActualSchedule = (state, homeId, zoneId) => {
  const home = state.homeList.homes.find(x => x.id === homeId);
  const selectedZone = home.zones.find(x => isMatchingZoneId(x.zoneId, zoneId));

  if (!selectedZone) {
    return [];
  }

  return flattenSchedule(selectedZone.schedule);
};

export const getScheduleEvents = (state, homeId, zoneId, selectedDay) => {
  const home = state.homeList.homes.find(x => x.id === homeId);

  if (!home) {
    return [];
  }

  const zone = home.zones.find(x => isMatchingZoneId(x.zoneId, zoneId));

  if (!zone?.schedule) {
    return [];
  }

  // Check for draft schedule first (local changes), then pending (in-flight), then actual
  const draftScheduleData = getDraftSchedule(state, homeId, zoneId);
  let schedule;
  if (draftScheduleData) {
    // Draft schedule is flat, need to unflatten it
    schedule = unflattenSchedule(draftScheduleData).map(x => ({
      ...x,
      events: x.events.map((y, i) => ({
        ...y,
        endTime:
          i + 1 < x.events.length ? x.events[i + 1].startTime : undefined,
      })),
    }));
  } else {
    schedule = getPendingSchedule(state, true, homeId, zoneId) || zone.schedule;
  }

  let todaysSchedule = schedule.find(x => x.day === selectedDay);

  if (!todaysSchedule) {
    return [];
  }

  const followingDay = getFollowingDay(todaysSchedule.day);

  const nextSchedule = schedule.find(x => x.day === followingDay);

  if (nextSchedule && nextSchedule.events.length > 0) {
    todaysSchedule = cloneDeep(todaysSchedule);

    todaysSchedule.events[todaysSchedule.events.length - 1].endTime =
      nextSchedule.events[0].startTime + 1440;
  }

  return todaysSchedule ? todaysSchedule.events : [];
};

export const getSelectedScheduleDay = state =>
  state.schedule.selectedScheduleDay;

export const getHpScheduleEventSetpoint = (state, homeId, zoneId, event) => {
  if (!event) {
    return 2000;
  }

  const zone = getZone(state, homeId, zoneId);
  const isComfort = event.setpoint === 'comfort';

  return isComfort
    ? zone.comfortTemperatureSetpoint
    : zone.reducdedTemperatureSetpoint;
};

export const getCurrentScheduleSetpoint = (state, homeId, zoneId, date) => {
  const day = getDayOfWeek(date);
  const minsSinceMidnight = dateTimeToMinutesSinceMidnight(date);

  const schedule = getScheduleEvents(state, homeId, zoneId, day);
  const earlier = schedule.filter(e => e.startTime < minsSinceMidnight);
  const result = earlier[earlier.length - 1];

  if (result) {
    if (isHpScheduleEvent(result)) {
      return getHpScheduleEventSetpoint(state, homeId, zoneId, result);
    }

    return result.setpoint;
  }

  const dayBefore = getPreviousDay(day);
  const scheduleBefore = getScheduleEvents(state, homeId, zoneId, dayBefore);
  const previousEvent = scheduleBefore[scheduleBefore.length - 1];

  if (previousEvent) {
    if (isHpScheduleEvent(previousEvent)) {
      return getHpScheduleEventSetpoint(state, homeId, zoneId, previousEvent);
    }

    return previousEvent.setpoint;
  }
  return 2000;
};

export const getModeAndUntilTimeForOverride = (state, homeId) => {
  // offline and holiday mode make this slightly more complex, some of
  // the reasons you should be able to send settings is because a device
  // is offline but you should be able to delete offline devices which lives
  // in the settings menu
  const zones = getZones(state, homeId);

  for (const z of zones) {
    const zoneId = z.zoneId;
    const mode = getMode(state, homeId, zoneId);
    const online = getPrtConnectionStatus(state, homeId, zoneId);
    const holidayModeActive = isInHolidayMode(state, homeId, zoneId);

    if (
      online &&
      (mode === MODES.OverrideNext ||
        mode === MODES.OverridePlusHours ||
        mode === MODES.DSRActive ||
        holidayModeActive)
    ) {
      return {
        // formerly this used the canSettingsBeSent selector but there's too many special cases to use that here
        canUseSettings: false,
        mode,
        nextEventTime: getNextEvent(state, homeId, zoneId)?.displayTime,
      };
    }
  }

  return {
    canUseSettings: true,
  };
};

export const isScheduleButtonDisabled = (state, homeId, zoneId) => {
  const mode = getMode(state, homeId, zoneId);

  if (mode !== MODES.Auto) {
    return true;
  }

  const schedule = getSchedule(state, homeId, zoneId);

  return schedule.length === 0;
};
